import "./userprofile.css";

import { DEFAULT_COUNTRY, getCountryCodeOptions } from "../../../service/service";
import { useCallback, useEffect, useState } from "react";

import { API_URL } from "../../../../env";
import CommonForm from "../../../shared-components/common-form/commonform";
import ImageUpload from "../../../shared-components/image-upload/imageUpload";
import axios from "axios";

const DEFAULT_EMAIL_LABEL = "Email";
const DEFAULT_COUNTRY_OPTIONS = [{ text: DEFAULT_COUNTRY.text, value: DEFAULT_COUNTRY.value }];
const GENDER_OPTIONS = [
  { text: "Male", value: "Male" },
  { text: "Female", value: "Female" },
  { text: "Transgender", value: "Transgender" }
];

const PROFILE_SIGNAL_CARDS = [
  {
    title: "Faster Booking",
    detail: "Complete contact details to reduce booking retries and callback delays."
  },
  {
    title: "Trusted Communication",
    detail: "Verified email and phone help providers coordinate service with confidence."
  },
  {
    title: "Accurate Identity",
    detail: "Keep profile data current for smoother service approvals and records."
  }
];

const getEmailLabel = (status) => {
  if (status === "verified") {
    return "Email (verified)";
  }

  if (status === "pending") {
    return "Email (verification pending)";
  }

  return DEFAULT_EMAIL_LABEL;
};

const getNestedValue = (obj, key) => {
  if (!obj || !key) {
    return undefined;
  }

  return key.split(".").reduce((acc, part) => {
    if (acc === null || acc === undefined) {
      return undefined;
    }
    return acc[part];
  }, obj);
};

const stripMdbFields = (input) => {
  if (Array.isArray(input)) {
    return input.map(stripMdbFields);
  }

  if (!input || typeof input !== "object") {
    return input;
  }

  return Object.keys(input).reduce((acc, key) => {
    if (!/^mdb/i.test(key)) {
      acc[key] = stripMdbFields(input[key]);
    }
    return acc;
  }, {});
};

const normalizeProfileData = (rawProfile) => {
  const cleaned = stripMdbFields(rawProfile || {});

  return {
    ...cleaned,
    userData: cleaned.userData || {},
    providerData: cleaned.providerData || {}
  };
};

const UserProfile = () => {
  const [formJSON, setFormJSON] = useState([
    { section: "user", label: "First Name", key: "firstName", type: "text", value: null, disabled: false, required: true, validation: true },
    { section: "user", label: "Middle Name", key: "middleName", type: "text", value: null, disabled: false, required: false, validation: false },
    { section: "user", label: "Last Name", key: "lastName", type: "text", value: null, disabled: false, required: true, validation: true },
    { section: "user", label: DEFAULT_EMAIL_LABEL, key: "email", type: "email", value: null, disabled: false, required: true, validation: true },
    { section: "user", label: "Date Of Birth", key: "dob", type: "date", value: null, disabled: false, required: false, validation: false },
    { section: "user", label: "Gender", key: "gender", type: "select", options: GENDER_OPTIONS, value: null, disabled: false, required: false, validation: false },
    { section: "user", label: "Phone Code", key: "primaryPhoneCountryCode", type: "select", options: DEFAULT_COUNTRY_OPTIONS, value: DEFAULT_COUNTRY.value, disabled: false, required: true, validation: false },
    { section: "user", label: "Phone Number", key: "primaryPhoneNumber", type: "text", value: null, disabled: false, required: true, validation: true },
    { section: "user", label: "Aadhar Number", key: "aadharNumber", type: "text", value: null, disabled: false, required: false, validation: true },
    { section: "user", label: "Pan Number", key: "panCardNumber", type: "text", value: null, disabled: false, required: false, validation: true }
  ]);

  const mapProfileToForm = useCallback((profile) => {
    const normalizedProfile = normalizeProfileData(profile);
    const emailValue = getNestedValue(normalizedProfile, "email");
    const isEmailVerified = getNestedValue(normalizedProfile, "isEmailVerified");

    setFormJSON((prevFormJSON) =>
      prevFormJSON.map((field) => {
        const fieldValue =
          field.key === "primaryPhoneNumber"
            ? getNestedValue(normalizedProfile, "primaryPhoneNumber") ?? getNestedValue(normalizedProfile, "mobile")
            : getNestedValue(normalizedProfile, field.key);

        const nextLabel = field.key === "email" ? getEmailLabel(emailValue ? (isEmailVerified ? "verified" : "pending") : "unknown") : field.label;

        return {
          ...field,
          label: nextLabel,
          value: fieldValue !== undefined && fieldValue !== null ? fieldValue : field.value
        };
      })
    );
  }, []);

  useEffect(() => {
    const loadCountryCodes = async () => {
      const options = await getCountryCodeOptions();
      setFormJSON((prevFormJSON) =>
        prevFormJSON.map((field) =>
          field.key === "primaryPhoneCountryCode"
            ? { ...field, options: options.length > 0 ? options : DEFAULT_COUNTRY_OPTIONS }
            : field
        )
      );
    };

    loadCountryCodes();
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("userId") || "2205816";

      if (!token) {
        return;
      }

      const url = `${API_URL}/profile/getProfileBasedOnUserId/${userId}`;

      if (!url) {
        return;
      }

      try {
        const response = await axios.get(url, {
          headers: {
            env: "local",
            token
          }
        });

        const profileData = response?.data?.responseData ?? response?.data;
        if (profileData && typeof profileData === "object") {
          mapProfileToForm(profileData);
        }
      } catch (error) {
        console.error("Failed to fetch user profile", error);
      }
    };

    loadUserProfile();
  }, [mapProfileToForm]);

  const handleProfileSaveSuccess = async () => {
    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("userId") || "2205816";

    if (!token) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/profile/getProfileBasedOnUserId/${userId}`, {
        headers: {
          env: "local",
          token
        }
      });

      const profileData = response?.data?.responseData ?? response?.data;
      if (profileData && typeof profileData === "object") {
        mapProfileToForm(profileData);
      }
    } catch (error) {
      console.error("Failed to refresh user profile", error);
    }
  };




  return (
    <div className="up-page">
      <section className="up-hero">
        <article className="up-hero-copy">
          <span className="up-eyebrow">Account Details</span>
          <h1>Personal Profile</h1>
          <p>
            Keep your personal details updated to make service bookings, communication,
            and provider interactions smoother across the platform.
          </p>

          <div className="up-signal-grid">
            {PROFILE_SIGNAL_CARDS.map((card) => (
              <article key={card.title}>
                <strong>{card.title}</strong>
                <span>{card.detail}</span>
              </article>
            ))}
          </div>
        </article>

        <article className="up-avatar-card">
          <ImageUpload />
          <div className="up-avatar-meta">
            <strong>Profile visibility</strong>
            <span>Photo and verified details build trust with providers.</span>
          </div>

          <ul className="up-avatar-list">
            <li>Use a clear front-facing profile photo.</li>
            <li>Keep phone number active for booking callbacks.</li>
            <li>Update identity details before service verification.</li>
          </ul>
        </article>
      </section>

      <CommonForm
        data={formJSON}
        postUrl={`/profile/updateProfile/${sessionStorage.getItem("userId")}`}
        heading="User Profile"
        onSuccess={handleProfileSaveSuccess}
      />
    </div>
  );
};

export default UserProfile;
